import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, CalendarDays, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Calendar = () => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API_URL}/events/all`);
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    
    // Previous month days
    for (let i = 0; i < startingDay; i++) {
      const prevDate = new Date(year, month, -startingDay + i + 1);
      days.push({ date: prevDate, isCurrentMonth: false });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }

    return days;
  };

  const getEventsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return events.filter(e => e.date === dateStr);
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const days = getDaysInMonth(currentDate);
  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date().toISOString().split('T')[0];

  const selectedDateEvents = selectedDate ? getEventsForDate(selectedDate) : [];

  return (
    <div className="min-h-screen py-20 px-6" data-testid="calendar-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Event <span className="gradient-text">Calendar</span>
          </h1>
          <p className="text-zinc-400 text-lg">
            Plan your nights out with DJ Shadow
          </p>
        </div>

        <div className="grid lg:grid-cols-[1fr_350px] gap-8">
          {/* Calendar */}
          <div className="glass-card rounded-2xl p-6">
            {/* Month Navigation */}
            <div className="flex items-center justify-between mb-6">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(-1)}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <h2 className="text-xl font-bold">{monthName}</h2>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigateMonth(1)}
                className="text-zinc-400 hover:text-white"
              >
                <ChevronRight className="w-5 h-5" />
              </Button>
            </div>

            {/* Weekday Headers */}
            <div className="grid grid-cols-7 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm text-zinc-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((day, index) => {
                const dateStr = day.date.toISOString().split('T')[0];
                const dayEvents = getEventsForDate(day.date);
                const hasEvents = dayEvents.length > 0;
                const isToday = dateStr === today;
                const isSelected = selectedDate && dateStr === selectedDate.toISOString().split('T')[0];

                return (
                  <button
                    key={index}
                    onClick={() => setSelectedDate(day.date)}
                    className={`
                      aspect-square p-1 rounded-xl transition-all relative
                      ${!day.isCurrentMonth ? 'text-zinc-700' : ''}
                      ${isToday ? 'ring-2 ring-[#D9F99D]' : ''}
                      ${isSelected ? 'bg-[#D9F99D] text-black' : 'hover:bg-white/5'}
                      ${hasEvents && !isSelected ? 'bg-[#D9F99D]/10' : ''}
                    `}
                  >
                    <span className="text-sm">{day.date.getDate()}</span>
                    {hasEvents && (
                      <div className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full ${
                        isSelected ? 'bg-black' : 'bg-[#D9F99D]'
                      }`} />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Details Sidebar */}
          <div className="glass-card rounded-2xl p-6">
            <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#D9F99D]" />
              {selectedDate 
                ? selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })
                : 'Select a date'
              }
            </h3>

            {selectedDate ? (
              selectedDateEvents.length > 0 ? (
                <div className="space-y-4">
                  {selectedDateEvents.map(event => (
                    <div 
                      key={event.id}
                      className="p-4 rounded-xl bg-white/5 border border-white/10"
                    >
                      <h4 className="font-bold mb-2">{event.title}</h4>
                      <div className="space-y-1 text-sm text-zinc-400">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          <span>{event.venue}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          <span>{event.location}</span>
                        </div>
                        {event.time && (
                          <div className="flex items-center gap-2">
                            <Clock className="w-3 h-3" />
                            <span>{event.time}</span>
                          </div>
                        )}
                      </div>
                      {event.ticket_url && (
                        <a
                          href={event.ticket_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-3 block w-full text-center py-2 rounded-lg bg-[#D9F99D] text-black font-bold text-sm hover:bg-[#BEF264] transition-colors"
                        >
                          Get Tickets
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-zinc-500 text-sm">No events on this date</p>
              )
            ) : (
              <p className="text-zinc-500 text-sm">Click on a date to see events</p>
            )}

            {/* Legend */}
            <div className="mt-6 pt-4 border-t border-white/10">
              <p className="text-xs text-zinc-500 mb-2">Legend</p>
              <div className="flex items-center gap-4 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-[#D9F99D]" />
                  <span className="text-zinc-400">Event</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full ring-2 ring-[#D9F99D]" />
                  <span className="text-zinc-400">Today</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Calendar;
